import Link from '@tiptap/extension-link';
import {
  isHrefStructurallyValid,
  isBlockedProtocol,
} from '@web-config-app/validation';

/**
 * Link extension wrapper that validates hrefs at render time and applies a CSS class
 * ("link-invalid") directly to <a> elements that fail validation.
 *
 * Why validate here instead of on input only?
 *  - Ensures any persisted invalid hrefs are visibly flagged when content is reloaded.
 */

// adds an error class directly to <a> when invalid
export const createValidatedLinkExtension = (allowedUriProtocols: string[]) =>
  Link.extend({
    addOptions() {
      return {
        ...this.parent?.(),
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {},
      };
    },
    renderHTML({ HTMLAttributes }) {
      const attrs = { ...HTMLAttributes } as Record<string, unknown>;
      // trim the incoming href (tiptap may pass through raw attribute values)
      const rawHref = typeof attrs.href === 'string' ? attrs.href.trim() : '';

      // Two-layer evaluation
      // 1. blocked: true if explicit scheme present and not in allow list
      // 2. structurallyValid: ensures non-empty and either relative OR well-formed absolute with allowed protocol
      const blocked =
        !!rawHref && isBlockedProtocol(rawHref, allowedUriProtocols);
      const structurallyValid =
        !!rawHref && isHrefStructurallyValid(rawHref, allowedUriProtocols);
      const isValid = !!rawHref && structurallyValid && !blocked;

      // Apply invalid class when failing either structural or policy checks.
      if (!isValid) {
        const existing = attrs.class ? String(attrs.class) : '';
        attrs.class = existing ? `${existing} link-invalid` : 'link-invalid';
      }
      return ['a', attrs, 0];
    },
  });
